/**
 * Hook V2 — useApprenantDocuments
 *
 * Phase wiring 3 : branche sur `GET /api/v1/v2/apprenants/:id/documents`.
 * Source : Drive (connector apprenant-documents).
 * Fallback mock si API indisponible.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export type DocStatus = 'signed' | 'pending' | 'missing' | 'not_applicable';

export interface DocItem {
  type: 'convention' | 'attestation' | 'certificat' | 'autre';
  label: string;
  status: DocStatus;
  driveUrl: string | null;
  generatedAt: string | null;
  signedAt: string | null;
}

export interface EmargementSummary {
  signedCount: number;
  expectedCount: number;
  signedHours: number;
  expectedHours: number;
  driveFolderUrl: string | null;
}

export interface ApprenantDocuments {
  apprenantId: string;
  docs: DocItem[];
  emargement: EmargementSummary;
}

const MARTINE_MOCK: ApprenantDocuments = {
  apprenantId: 'demo-martine',
  docs: [
    {
      type: 'convention',
      label: 'Convention de formation',
      status: 'signed',
      driveUrl: null,
      generatedAt: '2025-04-12',
      signedAt: '2025-04-13',
    },
    {
      type: 'attestation',
      label: "Attestation d'entrée",
      status: 'signed',
      driveUrl: null,
      generatedAt: '2025-04-15',
      signedAt: '2025-04-15',
    },
    {
      type: 'certificat',
      label: 'Certificat de réalisation',
      status: 'pending',
      driveUrl: null,
      generatedAt: null,
      signedAt: null,
    },
  ],
  emargement: {
    signedCount: 18,
    expectedCount: 30,
    signedHours: 18,
    expectedHours: 30,
    driveFolderUrl: null,
  },
};

async function fetchDocuments(
  id: string,
): Promise<ApprenantDocuments & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantDocuments>(`/api/v1/v2/apprenants/${id}/documents`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantDocuments(id: string): {
  data: (ApprenantDocuments & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-documents', id],
    queryFn: () => fetchDocuments(id),
    staleTime: 5 * 60_000, // Drive est cached 10 min côté API
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
